import { LearningPathsUsersApi, type LearningPathsUsersAttendeesModel } from "@simbelapp/order-sdk";
import { useLearningPathStore } from "./learning-path.store";
import { useLearningPathAttendeeStore } from "./learning-path-attendee.store";
import { useLearningPathTrainingsStore } from "./learning-path-trainings.store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useLearningPathAttendees } from "~/composables/learning-path/useLearningPathAttendees";
import { LearningPathAttendeesTabTypeEnum } from "~/utils/enums/learning-path.enums";
import type { IHeaderNav, IPaginationApi } from "~/utils/interfaces/table-interfaces";
import type { ILearningPathAttendeesStore } from "~/utils/interfaces/learning-path-interfaces";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFilters } from "~/composables/filters/useFilters";
import { usePagination } from "~/composables/paginations/usePagination";
import { ContextFiltersEnum } from "~/utils/enums/context-filters.enums";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";

/** Whether a search or a filter value narrows the « Ajouter des participants » table. */
function isAddAttendeesTableNarrowed(): boolean {
  const { paginateOptions } = usePagination(PaginationKeysEnum.LEARNING_PATH_ADD_ATTENDEES_MODAL_PAGINATION);
  const { activeFilters } = useFilters(ContextFiltersEnum.LEARNING_PATH_ADD_ATTENDEES_MODAL_CONTEXT);
  return !!paginateOptions.value.search || [...activeFilters.value].some((filter) => filter.selectedValues?.length);
}

/** Named users per write: orders caps JSON bodies at 100 kB, about 2,500 ids. */
const LEARNING_PATH_USERS_BATCH_SIZE = 1000;

export const useLearningPathAttendeesStore = defineStore("learningPathAttendeesStore", {
  state: (): ILearningPathAttendeesStore => ({
    currentLearningPathId: null,
    currentNav: null,
    learningPath: null,
    learningPathAttendeesFullDatas: null,
    learningPathAttendeesModal: false,
    learningPathAddAttendeesModal: false,
    learningPathAddAttendeesExitModal: false,
    learningPathDeleteAttendeesModal: false,
    learningPathNotifyAttendeesModal: false,
    learningPathAddAttendeesMultiSelect: {
      selectedItems: [],
      excludedItems: [],
    },
    learningPathAddAttendeesSelectAll: false,
    learningPathAttendeesMultiSelect: {
      selectedItems: [],
      excludedItems: [],
    },
    learningPathAttendeesSelectAll: false,
    addAttendeesToInvite: [],
  }),
  actions: {
    handleLearningPathAttendeesModal(isOpen: boolean) {
      this.learningPathAttendeesModal = isOpen;
    },
    handleLearningPathAddAttendeesModal(isOpen: boolean) {
      this.learningPathAddAttendeesModal = isOpen;
    },
    handleLearningPathAddAttendeesExitModal(isOpen: boolean) {
      this.learningPathAddAttendeesExitModal = isOpen;
    },
    handleLearningPathDeleteAttendeesModal(isOpen: boolean) {
      this.learningPathDeleteAttendeesModal = isOpen;
    },
    setCurrentLearningPathId(learningPathId: string | null) {
      this.currentLearningPathId = learningPathId;
    },
    setCurrentNav(currentNav: IHeaderNav) {
      this.currentNav = currentNav;
    },
    setLearningPathAttendeesFullDatas(data: IPaginationApi<LearningPathsUsersAttendeesModel> | null) {
      this.learningPathAttendeesFullDatas = data;
    },
    setLearningPath(learningPath: any) {
      this.learningPath = learningPath;
    },
    navigate(nav: IHeaderNav) {
      this.currentNav = nav;
    },
    /** `false` when a write fails: earlier batches may be saved, so the modal stays open for a retry. */
    async addAttendeesLearningPath(): Promise<boolean> {
      const feedback = useFeedback();

      if (!this.currentLearningPathId) {
        feedback.error("Aucun parcours sélectionné", "small");
        return false;
      }

      try {
        let userIds: string[];
        let selectAll = false;
        if (this.learningPathAddAttendeesSelectAll && isAddAttendeesTableNarrowed()) {
          // orders resolves `selectAll` over the whole company, blind to the table's filters and
          // search: send the users the modal resolved instead.
          userIds = this.addAttendeesToInvite.map((user) => user.userId);
        } else if (this.learningPathAddAttendeesSelectAll) {
          // Nothing narrows the table: orders resolves the same users, and keeps a whole company out of
          // the request body (JSON capped at 100 kB, about 2,500 ids).
          userIds = this.learningPathAddAttendeesMultiSelect?.excludedItems?.map((item: any) => item.userId) ?? [];
          selectAll = true;
        } else {
          userIds = this.learningPathAddAttendeesMultiSelect?.selectedItems?.map((item: any) => item.userId) ?? [];
        }

        const learningPathId = this.currentLearningPathId;
        // A select-all's exclusions go whole: split, each write would enrol the users the others exclude.
        const batches = selectAll
          ? [userIds]
          : Array.from({ length: Math.ceil(userIds.length / LEARNING_PATH_USERS_BATCH_SIZE) }, (_, index) =>
              userIds.slice(index * LEARNING_PATH_USERS_BATCH_SIZE, (index + 1) * LEARNING_PATH_USERS_BATCH_SIZE),
            );

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsUsersApi);
        for (const batch of batches) {
          await apiInstance.createLearningPathUsers({
            createLearningPathsUsersDto: { learningPathId, userIds: batch, selectAll },
          });
        }
        return true;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return false;
      }
    },

    async resetModal(fetch: boolean = true) {
      const learningPathStore = useLearningPathStore();
      const learningPathAttendeeStore = useLearningPathAttendeeStore();
      const learningPathTrainingStore = useLearningPathTrainingsStore();
      const learningPathAttendeesStore = useLearningPathAttendeesStore();
      const router = useRouter();
      const route = useRoute();
      const { learning_path_id, attendee_id, ...restQuery } = route.query;

      learningPathAttendeeStore.$reset();
      // Clear the polling interval before $reset(): $reset() only overwrites state
      // (pollingIntervalId -> null) and never calls clearInterval, which orphans the
      // running timer so stopCronFetchTrainings() can no longer clear it.
      learningPathTrainingStore.stopCronFetchTrainings();
      learningPathTrainingStore.$reset();
      learningPathAttendeesStore.$reset();

      if (fetch) {
        await learningPathStore.fetchLearningPathList();
      }

      await router.push({
        query: restQuery,
      });
    },
    resetLearningPathAddAttendeesMultiSelect() {
      if (this.learningPathAddAttendeesMultiSelect) {
        this.learningPathAddAttendeesMultiSelect.selectedItems = [];
        this.learningPathAddAttendeesMultiSelect.excludedItems = [];
      }

      this.learningPathAddAttendeesSelectAll = false;
      this.addAttendeesToInvite = [];
    },
    resetLearningPathAttendeesMultiSelect() {
      if (this.learningPathAttendeesMultiSelect) {
        this.learningPathAttendeesMultiSelect.selectedItems = [];
        this.learningPathAttendeesMultiSelect.excludedItems = [];
      }

      this.learningPathAttendeesSelectAll = false;
    },
    async loadAttendeesModal({
      learningPathId,
      currentNav = LearningPathAttendeesTabTypeEnum.ATTENDEES,
    }: {
      learningPathId: string;
      currentNav: LearningPathAttendeesTabTypeEnum;
    }) {
      if (learningPathId) {
        const router = useRouter();
        const route = useRoute();
        const { getLearningPathAttendeesNav } = useLearningPathAttendees();

        await router.push({ query: { ...route.query, learning_path_id: learningPathId } });

        const curr = getLearningPathAttendeesNav().find((header) => header.type === currentNav);
        if (curr) {
          this.setCurrentNav(curr);
        }
        this.handleLearningPathAttendeesModal(true);
      }
    },
    async deleteAttendeesFromLearningPath(learningPathId: string, keepSubscriptions: boolean) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsUsersApi);
        let removeAttendees;
        if (this.learningPathAttendeesSelectAll) {
          removeAttendees = this.learningPathAttendeesMultiSelect?.excludedItems?.map((item: any) => item.userId);
        } else {
          removeAttendees = this.learningPathAttendeesMultiSelect?.selectedItems?.map((item: any) => item.userId);
        }

        await apiInstance.removeLearningPathsUsersAttendees({
          learningPathId,
          removeAttendeesDto: {
            selectAll: this.learningPathAttendeesSelectAll,
            selectedAttendeeIds: removeAttendees,
            removeBookings: !keepSubscriptions,
          },
        });

        feedback.success("Les modifications ont été sauvegardées", "small");
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async fetchLearningPathAttendeesFullDatas(paginationOptions?: any, context?: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsUsersApi);
        const data = await apiInstance.getLearningPathsUsersAttendees({
          learningPathId: this.currentLearningPathId,
          ...paginationOptions,
          context,
        });

        this.setLearningPathAttendeesFullDatas({
          data: data.data,
          meta: {
            count: data.meta.count ?? 0,
            totalCount: null,
          },
        });
      } catch {
        this.setLearningPathAttendeesFullDatas(null);
      }
    },
  },
});
